import { Fragment, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import copy from '../demo/fixtures/copy.json'
import { useChain, type ChainStepId } from '../demo/selectors/chain'
import { GlossaryRenderPass, GlossaryText } from './Glossary'
import styles from './ChainRail.module.css'

function CheckIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5 9 17.5 20 6.5" /></svg>
}

function SparkIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4z" /></svg>
}

function ArrowIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12h15M13 6l6 6-6 6" /></svg>
}

export function ChainRail() {
  const location = useLocation()
  const { pathname } = location
  const navigationFocus = (location.state as { chainFocus?: ChainStepId } | null)?.chainFocus ?? null
  const [focusOverride, setFocusOverride] = useState<ChainStepId | null>(navigationFocus)
  const chain = useChain(focusOverride)
  const railRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const rail = railRef.current
    if (!rail) return
    const update = () => document.documentElement.style.setProperty('--rail-h', `${rail.getBoundingClientRect().height}px`)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(rail)
    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--rail-h')
    }
  }, [])

  const labels = copy.chain.labels
  const loading = chain.variant === 'loading'
  const error = chain.variant === 'error'
  const glossaryPassKey = `${pathname}|${chain.focusId}|${chain.variant}|${chain.focus.trio.action}|${chain.focus.trio.basis}|${chain.focus.trio.result}`

  return (
    <GlossaryRenderPass scopeKey={glossaryPassKey}>
      <div ref={railRef} className={styles.rail} data-scope="chain-rail" data-variant={chain.variant}>
        <div className={styles.inner}>
          <div className={styles.steps} data-role="steps">
            {chain.steps.map((step, index) => (
              <Fragment key={step.id}>
                <Link
                  to={step.route}
                  state={{ chainFocus: step.id }}
                  className={`${styles.node} ${styles[step.node]}`}
                  data-role={`node-${step.id}`}
                  data-state={step.node}
                  onClick={() => setFocusOverride(step.id)}
                >
                  <em>{step.node === 'done' ? <CheckIcon /> : step.id}</em>
                  <span>{step.shortName}</span>
                </Link>
                {index < chain.steps.length - 1 ? (
                  <div className={`${styles.line} ${step.node === 'done' ? styles.lineDone : ''}`} />
                ) : null}
              </Fragment>
            ))}
          </div>
          <div className={styles.currentStep}>
            <span className={styles.avatar} data-role="avatar"><SparkIcon /></span>
            <div className={styles.text}>
              <p><b>{labels.action}</b>{loading ? <span className={styles.skeleton} style={{ width: `${copy.chain.skeletonWidths[0]}%` }} /> : <GlossaryText text={chain.focus.trio.action} />}</p>
              <p className={styles.basis}><b>{labels.basis}</b>{loading ? <span className={styles.skeleton} style={{ width: `${copy.chain.skeletonWidths[1]}%` }} /> : error ? <span className={styles.error}>{chain.focus.trio.basis}</span> : <GlossaryText text={chain.focus.trio.basis} />}</p>
              <p className={`${styles.result} ${chain.variant === 'no-data' ? styles.muted : ''}`}><b>{labels.result}</b>{loading ? <span className={styles.skeleton} style={{ width: `${copy.chain.skeletonWidths[2]}%` }} /> : error ? <span className={styles.error}>{chain.focus.trio.result}</span> : <GlossaryText text={chain.focus.trio.result} paren />}</p>
            </div>
            {chain.next ? <Link className={styles.next} data-role="next-step" to={chain.next.to}>{chain.next.label}<ArrowIcon /></Link> : <span className={styles.noNext} data-role="next-step">{copy.chain.states.linkError.noNext}</span>}
          </div>
        </div>
      </div>
    </GlossaryRenderPass>
  )
}
